import axios from 'axios';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { Desktop, Mobile } from '../../../App';
import Banner from '../../../components/common/Banner';
import { SidebarModal } from '../../../components/common/Sidebar/Sidebar';
import SidebarNav from '../../../components/common/SidebarNav';
import {
  SidebarIcon,
  SidebarNavLinks,
  useOnClickOutside
} from '../../../helpers/helperFunctions';
import { useWindowWide } from '../../../helpers/wideScreen';
import { P2 } from '../../../styles/FontStyles';
import {
  ArrowPolygon,
  Flexed,
  UserImageCircle
} from '../../../styles/GlobalComponents';
import { color, colors, globalImages } from '../../../styles/GlobalStyles';
import {
  Icon,
  IconHome,
  IconMenu,
  IconNotifications,
  IconTelegramNoBorder
} from '../../../styles/Icons';
import { FlexCenter } from '../../../styles/stylesStyled';
import Notifications from '../../../views/Notifications/Notifications';

import { DASHBOARD, MY_HUBS } from '../../../routes/routes';
import {
  MainNav,
  Menu,
  TopNavigation,
  TopNavigationMain
} from '../BackofficeLayoutStyled';

import { AnimatePresence } from 'framer-motion';
import { useAppSelector } from '../../../app/hooks';
import { getNotifications } from '../../../slices/notifications-slice';
import { ProfileDropdown } from './ProfileDropdown';
import { ShowHide } from './ShowHide';
import { UserTypes } from '../../../types/loginTypes';
import { getCurrentLicense } from '../../../slices/auth-slice';
import { SidebarNavContent } from './SidebarNavContent';

const APPLICATION_SERVER_KEY = process.env.REACT_APP_APPLICATION_SERVER_KEY;

export const TopNav = () => {
  const { userInfo } = useAppSelector((state) => state.auth);
  const wide = useWindowWide(800);
  const [sidebar, setSidebar] = useState(false);
  const ref = useRef(null);
  const ref2 = useRef(null);
  const history = useHistory();
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const [centerMenu, setCenterMenu] = useState(false);
  const dispatch = useDispatch();

  const location = useLocation();
  const pathname = location.pathname;

  const [notificationsModal, setNotificationsModal] = useState(false);
  const { t } = useTranslation('layout');

  useOnClickOutside(ref, () => menuIsOpen && setMenuIsOpen(false));
  useOnClickOutside(ref2, () => centerMenu && setCenterMenu(false));

  useEffect(() => {
    if (userInfo.user) {
      dispatch(getCurrentLicense());
    }
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker
        .register('/sw.js')
        .then(
          function (registration) {
            // Registration was successful
            // console.log(
            //   'ServiceWorker registration successful with scope: ',
            //   registration.scope
            // );
            subscribe();
          },
          function (err) {
            // registration failed :(
            // console.log('ServiceWorker registration failed: ', err);
          }
        )
        .catch(() => {
          // console.log('----');
        });
    }
  }, []);

  async function subscribe() {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then(function (reg) {
        let token = userInfo?.token?.accessToken;
        if (!token) return;
        const config = {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            'Access-Control-Allow-Origin': '*'
          }
        };

        reg?.pushManager
          ?.subscribe({
            userVisibleOnly: true,
            applicationServerKey: APPLICATION_SERVER_KEY
          })
          .then(function (sub) {
            // console.log('Endpoint URL: ', sub.endpoint);
            const data = JSON.parse(JSON.stringify(sub));
            axios.post(
              `/user/notification/web/device`,
              {
                endpoint: data.endpoint,
                p256dh: data.keys.p256dh,
                auth: data.keys.auth
              },
              config
            );
          })
          .catch(function (e) {
            if (Notification.permission === 'denied') {
              console.log('Permission for notifications was denied');

              axios.delete(`/user/notification/web/device`, config);
            } else {
              console.error('Unable to subscribe to push', e);
            }
          });
      });
    }
  }

  const showHide = ShowHide();
  const sidebarNavLinks = SidebarNavLinks();

  return (
    <AnimatePresence>
      {showHide && (
        <TopNavigation
          key='TopNavigation'
          initial={{ transform: 'translateY(-70px)' }}
          animate={{ transform: 'translateY(0px)' }}
          exit={{ transform: 'translateY(-70px)' }}
          transition={{
            duration: 0.2
          }}>
          <TopNavigationMain>
            <img
              src={globalImages.logoOnlyLetter}
              alt='logo'
              style={{
                height: 40,
                cursor: 'pointer'
              }}
              onClick={() =>
                history.push(
                  userInfo.userType === UserTypes.Regular ? DASHBOARD : MY_HUBS
                )
              }
            />

            <Desktop>
              <Flexed gap='20px'>
                {sidebarNavLinks.map(({ link, name, disabled, hidden }, i) => {
                  if (hidden) return null;

                  return (
                    <Link
                      to={link}
                      key={i}
                      style={{
                        pointerEvents: disabled ? 'none' : 'auto'
                      }}>
                      <Flexed
                        height='50px'
                        center
                        style={{
                          opacity: disabled ? 0.3 : 1
                        }}>
                        <Icon
                          m='0 15px'
                          mSM='0 20px'
                          w='32px'
                          color={
                            link === pathname
                              ? colors.primary900
                              : colors.neutral500
                          }>
                          <SidebarIcon link={name} />
                        </Icon>
                      </Flexed>
                    </Link>
                  );
                })}
              </Flexed>
            </Desktop>

            <Mobile>
              <div ref={ref2}>
                <Icon
                  mr='10'
                  pointer
                  onClick={() => setCenterMenu(!sidebar)}
                  color={colors.neutral0}>
                  <IconHome />
                </Icon>
                <Menu isOpen={centerMenu} center>
                  <SidebarNavContent onClose={setCenterMenu} />
                </Menu>
              </div>
            </Mobile>

            <MainNav>
              <Desktop>
                <a
                  href='https://t.me/GenesisGlobal_World'
                  target='_blank'
                  rel='noreferrer'>
                  <Icon
                    pointer
                    w='34px'
                    ml='20'
                    mr='20'
                    mlSM='10'
                    color={colors.primary900}>
                    <IconTelegramNoBorder />
                  </Icon>
                </a>
              </Desktop>

              <Desktop>
                <div style={{ position: 'relative' }}>
                  <Banner
                    unseen={userInfo.unseenNotificationCount}
                    notification={true}
                  />

                  <Icon
                    onClick={() => {
                      setNotificationsModal(!notificationsModal);
                      dispatch(getNotifications());
                    }}
                    pointer
                    mr='20'
                    mrSM='10'
                    color={colors.primary900}>
                    <IconNotifications />
                  </Icon>
                </div>
              </Desktop>

              <FlexCenter ref={ref}>
                <div
                  onClick={() => {
                    setMenuIsOpen(!menuIsOpen);
                  }}>
                  <UserImageCircle
                    image={userInfo?.user?.profileImageUrl}
                    name={userInfo?.user?.name}
                    surname={userInfo?.user?.surname}
                    size='40px'
                    fontSize='16px'
                    margin={wide ? '0 16px 0 0' : '0'}
                  />
                </div>

                <div
                  style={{
                    fontSize: '16px',
                    lineHeight: '34px',
                    position: 'relative'
                  }}>
                  <div
                    style={{
                      display: wide ? 'flex' : 'none',
                      alignItems: 'center',
                      cursor: 'pointer'
                    }}
                    onClick={() => {
                      setMenuIsOpen(!menuIsOpen);
                    }}>
                    <P2 color={colors.neutral900} mr='10'>
                      {userInfo?.user?.name}
                    </P2>

                    <ArrowPolygon direction={menuIsOpen ? 'up' : 'down'} />
                  </div>
                  <div>
                    <ProfileDropdown
                      menuIsOpen={menuIsOpen}
                      userInfo={userInfo}
                      setMenuIsOpen={setMenuIsOpen}
                    />
                  </div>
                </div>
              </FlexCenter>
            </MainNav>
          </TopNavigationMain>
          <SidebarNav open={sidebar} onClose={setSidebar} />
        </TopNavigation>
      )}
      <SidebarModal
        isOpen={notificationsModal}
        setOpen={setNotificationsModal}
        title={t('notifications.title')}
        sidebarWidth={'508px'}>
        <Notifications setNotificationsModal={setNotificationsModal} />
      </SidebarModal>
    </AnimatePresence>
  );
};
